/**
 * @file WithAIGMPOverview.jsx
 * @description withGMP 플랫폼에 AI 챗봇 모듈을 추가한 with-AI-GMP 솔루션 소개 페이지입니다.
 * 자연어 질의, 이상 감지, 작업 가이드, 리포트 자동화 등 AI 기능을 소개합니다.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  ChartBar,
  FileText,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Zap,
} from 'lucide-react';

/* ──────────────────────────────────────────────
   스크롤 내비게이션 섹션 정의
   ────────────────────────────────────────────── */
const SCROLL_NAV_SECTIONS = [
  { id: 'waigmp-sec-features', label: 'AI 기능' },
  { id: 'waigmp-sec-chat', label: '챗봇 데모' },
  { id: 'waigmp-sec-arch', label: '연동 구조' },
  { id: 'waigmp-sec-usecases', label: '활용 시나리오' },
  { id: 'waigmp-sec-process', label: '구축 전략' },
  { id: 'waigmp-sec-cta', label: '도입 문의' },
];

/* ──────────────────────────────────────────────
   AI 핵심 기능 목록
   ────────────────────────────────────────────── */
const AI_FEATURES = [
  {
    num: '01',
    category: 'NATURAL LANGUAGE QUERY',
    title: '자연어 데이터 조회',
    Icon: MessageSquare,
    desc: '"이번 주 RWS 칭량 이탈 건수 보여줘" 처럼 말하듯 질문하면 AI가 즉시 데이터를 찾아 답합니다. SQL·화면 탐색 없이 현장 언어로 원하는 수치를 확인합니다.',
  },
  {
    num: '02',
    category: 'PROCEDURAL GUIDANCE',
    title: 'SOP 기반 작업 가이드',
    Icon: FileText,
    desc: 'GMP SOP와 전자제조기록을 학습한 AI가 단계별 작업 절차를 즉시 안내합니다. 신규 인력 온보딩과 일탈 발생 시 대처 절차를 실시간으로 지원합니다.',
  },
  {
    num: '03',
    category: 'REPORT AUTOMATION',
    title: '자동 리포트 생성',
    Icon: ChartBar,
    desc: '배치 품질 요약, 월간 생산 실적, 에너지 사용 리포트를 AI가 자동으로 작성합니다. 보고서 작성 시간을 줄이고 데이터 기반 의사결정을 지원합니다.',
  },
];

/* ──────────────────────────────────────────────
   챗봇 데모 대화 예시
   ────────────────────────────────────────────── */
const CHAT_DEMO = [
  {
    role: 'user',
    text: '오늘 MES 기준 배치 완료 건수와 불량률 알려줘.',
  },
  {
    role: 'ai',
    text: '오늘(5/11) 기준 배치 완료 **12건**, 불량 발생 **1건** (불량률 **8.3%**)입니다.\n\n불량 배치는 **MFG-20240511-009**이며, 공정 B 구간 온도 이탈(+2.4°C)이 원인으로 기록되었습니다. 해당 배치 상세 조회를 원하시면 말씀해주세요.',
    tags: ['MES 연동', '실시간'],
  },
  {
    role: 'user',
    text: '이번 달 RWS 칭량 이탈 Top 3 원료 알려줘.',
  },
  {
    role: 'ai',
    text: '5월 칭량 이탈 건수 상위 원료입니다.\n\n① **원료-A (REF-0023)** — 7건 (허용 ±0.5g 초과)\n② **원료-C (REF-0041)** — 4건\n③ **원료-F (REF-0088)** — 3건\n\n원료-A의 경우 저울 #03 교정 일정이 12일 초과된 상태입니다. 교정 작업지시를 생성할까요?',
    tags: ['RWS 연동', 'CMMS 연동'],
  },
];

/* ──────────────────────────────────────────────
   withGMP 연동 모듈 아키텍처
   ────────────────────────────────────────────── */
const ARCH_MODULES = [
  { code: 'RWS', label: '칭량관리', color: '#c98a2b' },
  { code: 'MES', label: '제조실행', color: '#2a6fdb' },
  { code: 'WMS', label: '창고물류', color: '#1f8a5b' },
  { code: 'withEAI', label: '시스템연계', color: '#b54a1f' },
  { code: 'CMMS', label: '설비보전', color: '#216b52' },
  { code: 'FEMS', label: '에너지관리', color: '#2f8f6b' },
];

/* ──────────────────────────────────────────────
   역할별 활용 시나리오
   ────────────────────────────────────────────── */
const USE_CASES = [
  {
    role: '품질관리 담당자',
    icon: ShieldCheck,
    accent: '#2457d6',
    scenarios: [
      'AI에게 "배치 OOT 현황 요약해줘" 한 문장으로 리포트 즉시 생성',
      '공정 이탈 발생 시 SOP 기반 대처 절차 실시간 안내',
      '규격 초과 원료의 과거 이탈 패턴 자동 분석',
    ],
  },
  {
    role: '생산관리 담당자',
    icon: TrendingUp,
    accent: '#1f8a5b',
    scenarios: [
      '"오늘 생산 진행률 어때?" 질문으로 라인별 현황 즉시 파악',
      '생산 스케줄 충돌·지연 예측 알림 수신',
      '작업지시 누락·대기 배치 AI 자동 감지 및 알림',
    ],
  },
  {
    role: '경영진 / 관리자',
    icon: Zap,
    accent: '#7c3aed',
    scenarios: [
      '월간 생산·품질·에너지 KPI 대시보드 AI 요약 리포트',
      '"전월 대비 불량률 변화 원인 분석해줘" 자연어 질의',
      '규제 감사 대비 전자기록 요약 자동 생성',
    ],
  },
];

/* ──────────────────────────────────────────────
   구축 단계
   ────────────────────────────────────────────── */
const PROCESS_STEPS = [
  { n: '01', t: '데이터 현황 진단', d: 'withGMP 연동 모듈 데이터 품질 및 AI 학습 가능성 평가' },
  { n: '02', t: 'AI 모델 설계', d: '현장 SOP·도메인 지식 기반 챗봇 및 이상 감지 모델 설계' },
  { n: '03', t: '파일럿 구축', d: '핵심 질의·알림 시나리오 구현 및 현장 검증' },
  { n: '04', t: '전사 확대', d: '전 부서 롤아웃 및 피드백 반영 고도화' },
  { n: '05', t: '지속 운영', d: 'AI 성능 모니터링, 데이터 재학습, 기능 추가' },
];

/* ──────────────────────────────────────────────
   공통 섹션 헤더
   ────────────────────────────────────────────── */
function SectionHead({ eyebrow, title, sub }) {
  return (
    <div className="waigmp-section-head">
      <p className="waigmp-eyebrow">{eyebrow}</p>
      <h2 className="waigmp-section-title">{title}</h2>
      {sub && <p className="waigmp-section-sub">{sub}</p>}
    </div>
  );
}

/* ──────────────────────────────────────────────
   우측 고정 스크롤 내비게이션
   ────────────────────────────────────────────── */
function ScrollNav() {
  const navRef = useRef(null);
  const [active, setActive] = useState(SCROLL_NAV_SECTIONS[0].id);
  const [isOverDark, setIsOverDark] = useState(false);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    let frameId = 0;
    const update = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const navRect = nav.getBoundingClientRect();
        const over = ['.waigmp-hero', '.waigmp-cta'].some((sel) => {
          const section = document.querySelector(sel);
          if (!section) return false;
          const sRect = section.getBoundingClientRect();
          return navRect.top < sRect.bottom && navRect.bottom > sRect.top;
        });
        setIsOverDark((prev) => (prev === over ? prev : over));
      });
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { cancelAnimationFrame(frameId); window.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, []);

  useEffect(() => {
    let frameId = 0;
    const updateActive = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const activationLine = window.innerHeight * 0.42;
        const current = SCROLL_NAV_SECTIONS.reduce((matchedId, { id }) => {
          const el = document.getElementById(id);
          if (!el) return matchedId;
          return el.getBoundingClientRect().top <= activationLine ? id : matchedId;
        }, SCROLL_NAV_SECTIONS[0].id);
        setActive((prev) => (prev === current ? prev : current));
      });
    };
    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);
    return () => { cancelAnimationFrame(frameId); window.removeEventListener('scroll', updateActive); window.removeEventListener('resize', updateActive); };
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' });
  };

  return (
    <nav ref={navRef} className={`scroll-nav${isOverDark ? ' is-over-hero' : ''}`} aria-label="with-AI-GMP section navigation">
      <ul>
        {SCROLL_NAV_SECTIONS.map(({ id, label }) => (
          <li key={id} className={active === id ? 'is-active' : ''}>
            <button type="button" onClick={() => goTo(id)} aria-current={active === id ? 'true' : undefined}>
              <span className="sn-label">{label}</span>
              <span className="sn-marker" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/* ──────────────────────────────────────────────
   Hero 섹션 — AI 챗봇 인터페이스 미리보기 포함
   ────────────────────────────────────────────── */
function WithAIGMPHero({ onNavigate }) {
  const trustItems = [
    { value: 'withGMP', label: '기반 플랫폼', sub: 'RWS · MES · WMS +' },
    { value: 'AI 챗봇', label: '핵심 추가 모듈', sub: '자연어 질의·분석' },
    { value: '자동화', label: '리포트 생성', sub: '배치·KPI 요약' },
  ];

  return (
    <section className="waigmp-hero">
      {/* 배경 그리드 패턴 */}
      <svg aria-hidden="true" className="waigmp-hero-bg-svg" viewBox="0 0 1600 600" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="waigmp-hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="0.8" />
          </pattern>
          <radialGradient id="waigmp-hero-glow" cx="30%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.35" />
            <stop offset="100%" stopColor="transparent" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="1600" height="600" fill="url(#waigmp-hero-grid)" />
        <rect width="1600" height="600" fill="url(#waigmp-hero-glow)" />
      </svg>

      <div className="container">
        <div className="waigmp-hero-inner">
          {/* 좌측: 텍스트 */}
          <div className="waigmp-hero-copy">
            <div className="waigmp-hero-badge">
              <span className="waigmp-hero-badge-dot" aria-hidden="true" />
              withGMP Enhanced · AI Powered
            </div>

            <h1 className="waigmp-hero-title">
              GMP 플랫폼에
              <br />
              <span className="waigmp-hero-title-accent">AI 어시스턴트</span>를 더하다
            </h1>

            <p className="waigmp-hero-desc">
              <strong>with-AI-GMP</strong>는 withGMP의 모든 운영 데이터에
              AI 챗봇 모듈을 결합합니다.
              <br />
              자연어로 묻고, SOP를 즉시 안내하고, 보고서를 자동으로 완성합니다.
            </p>

            <div className="waigmp-hero-actions">
              <button type="button" className="waigmp-btn waigmp-btn-primary" onClick={() => onNavigate('/contact')}>
                도입 상담 요청 <ArrowRight size={16} />
              </button>
              <button type="button" className="waigmp-btn waigmp-btn-ghost" onClick={() => { const el = document.getElementById('waigmp-sec-chat'); if (el) window.scrollTo({ top: el.offsetTop - 40, behavior: 'smooth' }); }}>
                챗봇 데모 보기
              </button>
            </div>

            <div className="waigmp-trust-strip">
              {trustItems.map((item) => (
                <div key={item.value} className="waigmp-trust-item">
                  <span className="waigmp-trust-value">{item.value}</span>
                  <span className="waigmp-trust-label">{item.label}</span>
                  <span className="waigmp-trust-sub">{item.sub}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 우측: 미니 채팅 UI 미리보기 */}
          <div className="waigmp-hero-preview" aria-hidden="true">
            <div className="waigmp-preview-window">
              <div className="waigmp-preview-titlebar">
                <span className="waigmp-preview-dot red" />
                <span className="waigmp-preview-dot yellow" />
                <span className="waigmp-preview-dot green" />
                <span className="waigmp-preview-title">
                  <Bot size={13} /> with-AI-GMP Assistant
                </span>
              </div>
              <div className="waigmp-preview-chat">
                <div className="waigmp-preview-msg user">오늘 배치 완료 건수 알려줘</div>
                <div className="waigmp-preview-msg ai">
                  <span className="waigmp-preview-ai-icon"><Sparkles size={12} /></span>
                  오늘 배치 완료 <strong>12건</strong>, 불량 <strong>1건</strong> (8.3%)
                  <br />
                  <span className="waigmp-preview-tag">MES 연동</span>
                  <span className="waigmp-preview-tag">실시간</span>
                </div>
                <div className="waigmp-preview-msg user">RWS 이탈 원료 Top 3 보여줘</div>
                <div className="waigmp-preview-msg ai">
                  <span className="waigmp-preview-ai-icon"><Sparkles size={12} /></span>
                    ① 원료-A — 7건 <br/> ② 원료-C — 4건  <br/> ③ 원료-F — 3건
                  <br />
                  <span className="waigmp-preview-tag">RWS 연동</span>
                </div>
                <div className="waigmp-preview-msg user">최근 한달 공정 진행율 엑셀로 만들어줘</div>
                <div className="waigmp-preview-msg ai">
                  <span className="waigmp-preview-ai-icon"><Sparkles size={12} /></span>
                  4/11 ~ 5/11 공정 진행율 데이터를 정리했습니다.
                  <div className="waigmp-preview-excel">
                    <span className="waigmp-preview-excel-icon">📊</span>
                    <span className="waigmp-preview-excel-name">공정진행율_2504-2505.xlsx</span>
                    <span className="waigmp-preview-excel-btn">내보내기</span>
                  </div>
                  <span className="waigmp-preview-tag">MES 연동</span>
                  <span className="waigmp-preview-tag">Excel</span>
                </div>
              </div>
              <div className="waigmp-preview-input">
                <span>AI에게 질문하세요…</span>
                <Send size={14} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   AI 핵심 기능 섹션
   ────────────────────────────────────────────── */
function WithAIGMPFeatures() {
  return (
    <section className="waigmp-features">
      <div className="container">
        <SectionHead
          eyebrow="AI CAPABILITIES"
          title="withGMP 데이터 위에서 작동하는 3가지 AI 기능"
          sub="챗봇 하나로 조회·가이드·리포트를 모두 처리합니다"
        />
        <div className="waigmp-features-grid">
          {AI_FEATURES.map((feat) => {
            const Icon = feat.Icon;
            return (
              <article key={feat.num} className="waigmp-feat-card">
                <div className="waigmp-feat-top">
                  <span className="waigmp-feat-num">{feat.num}</span>
                  <span className="waigmp-feat-category">{feat.category}</span>
                </div>
                <div className="waigmp-feat-icon">
                  <Icon size={26} strokeWidth={1.8} />
                </div>
                <h3 className="waigmp-feat-title">{feat.title}</h3>
                <p className="waigmp-feat-desc">{feat.desc}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   챗봇 대화 데모 섹션
   ────────────────────────────────────────────── */
function WithAIGMPChatDemo() {
  const [visible, setVisible] = useState(1);

  useEffect(() => {
    if (visible >= CHAT_DEMO.length) return undefined;
    const timer = setTimeout(() => setVisible((v) => v + 1), 1200);
    return () => clearTimeout(timer);
  }, [visible]);

  /* 섹션에 진입할 때 애니메이션을 처음부터 재시작합니다. */
  const sectionRef = useRef(null);
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(1); },
      { threshold: 0.3 },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="waigmp-chat-demo">
      <div className="container">
        <div className="waigmp-chat-demo-inner">
          {/* 좌측 설명 */}
          <div className="waigmp-chat-copy">
            <SectionHead
              eyebrow="CHATBOT DEMO"
              title="말하듯 묻고, 데이터로 답한다"
              sub={null}
            />
            <p className="waigmp-chat-copy-body">
              withGMP 전체 모듈의 데이터를 AI가 실시간으로 조회합니다.
              복잡한 화면 탐색이나 쿼리 없이, 현장 언어 그대로 질문하면 즉시 답을 드립니다.
            </p>
            <ul className="waigmp-chat-points">
              <li><BrainCircuit size={16} /> 문맥을 이해하는 멀티턴 대화</li>
              <li><ShieldCheck size={16} /> GMP 도메인 학습 기반 정확한 응답</li>
              <li><Zap size={16} /> RWS · MES · WMS · CMMS 실시간 연동</li>
            </ul>
          </div>

          {/* 우측 채팅 창 */}
          <div className="waigmp-chat-window">
            <div className="waigmp-chat-header">
              <span className="waigmp-chat-header-icon"><Bot size={18} /></span>
              <div>
                <strong>with-AI-GMP Assistant</strong>
                <span className="waigmp-chat-status">● 온라인</span>
              </div>
            </div>
            <div className="waigmp-chat-body">
              {CHAT_DEMO.slice(0, visible).map((msg, i) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className={`waigmp-chat-msg waigmp-chat-msg--${msg.role}`}
                >
                  {msg.role === 'ai' && (
                    <span className="waigmp-chat-ai-avatar"><Sparkles size={14} /></span>
                  )}
                  <div className="waigmp-chat-bubble">
                    {msg.text.split('\n').map((line, li) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={li}>
                        {/* **굵게** 마크업을 실제 bold로 처리합니다. */}
                        {line.split(/(\*\*[^*]+\*\*)/g).map((part, pi) =>
                          part.startsWith('**') && part.endsWith('**')
                            // eslint-disable-next-line react/no-array-index-key
                            ? <strong key={pi}>{part.slice(2, -2)}</strong>
                            // eslint-disable-next-line react/no-array-index-key
                            : <span key={pi}>{part}</span>
                        )}
                        {li < msg.text.split('\n').length - 1 && <br />}
                      </span>
                    ))}
                    {msg.tags && (
                      <div className="waigmp-chat-tags">
                        {msg.tags.map((tag) => <span key={tag}>{tag}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="waigmp-chat-footer">
              <input type="text" placeholder="AI에게 질문하세요…" readOnly />
              <button type="button" aria-label="전송"><Send size={16} /></button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   withGMP 연동 아키텍처 다이어그램 — 원형 배치
   with-AI-GMP가 중앙, 6개 모듈이 원형으로 감쌉니다.
   ────────────────────────────────────────────── */
function WithAIGMPArchDiagram() {
  const HUB_CX = 210;
  const HUB_CY = 210;
  const ORBIT_R = 148;
  const HUB_R   = 66;
  const CARD_W  = 96;
  const CARD_H  = 44;

  /* 6개 모듈을 60° 간격으로 배치합니다. -90°(12시 방향)에서 시작합니다. */
  const modules = ARCH_MODULES.map((mod, i) => {
    const angleRad = (-90 + i * 60) * (Math.PI / 180);
    return {
      ...mod,
      mx: HUB_CX + ORBIT_R * Math.cos(angleRad),
      my: HUB_CY + ORBIT_R * Math.sin(angleRad),
    };
  });

  return (
    <svg viewBox="0 0 420 420" className="waigmp-arch-svg" aria-label="with-AI-GMP 연동 아키텍처 다이어그램">
      <defs>
        <linearGradient id="waigmp-ai-grad" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#6d28d9" />
          <stop offset="100%" stopColor="#2457d6" />
        </linearGradient>
        <filter id="waigmp-hub-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* 궤도 점선 원 */}
      <circle
        cx={HUB_CX} cy={HUB_CY} r={ORBIT_R}
        fill="none" stroke="#d0d5e8" strokeWidth="1" strokeDasharray="6 4"
      />

      {/* 연결선 — 렌더 순서: 선 → 허브 → 카드 (허브·카드가 선 끝을 가림) */}
      {modules.map((mod) => (
        <line
          key={`ln-${mod.code}`}
          x1={HUB_CX} y1={HUB_CY}
          x2={mod.mx} y2={mod.my}
          stroke={mod.color}
          strokeWidth="1.8"
          strokeDasharray="5 3"
          opacity="0.55"
        />
      ))}

      {/* 중앙 AI 허브 원 */}
      <circle cx={HUB_CX} cy={HUB_CY} r={HUB_R} fill="url(#waigmp-ai-grad)" filter="url(#waigmp-hub-glow)" />
      <text x={HUB_CX} y={HUB_CY - 14} textAnchor="middle" fill="#fff" fontSize="12.5" fontWeight="800">with-AI-GMP</text>
      <text x={HUB_CX} y={HUB_CY + 4}  textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="9.5">AI Chatbot Module</text>
      <text x={HUB_CX} y={HUB_CY + 20} textAnchor="middle" fill="rgba(255,255,255,0.62)" fontSize="8.5">자연어 질의 · 리포트 자동화</text>

      {/* 모듈 카드 */}
      {modules.map((mod) => {
        const x = mod.mx - CARD_W / 2;
        const y = mod.my - CARD_H / 2;
        return (
          <g key={mod.code}>
            <rect x={x} y={y} width={CARD_W} height={CARD_H} rx="8"
              fill="#fff" stroke={mod.color} strokeWidth="1.3" />
            <text x={x + CARD_W / 2} y={y + 17} textAnchor="middle" fill={mod.color} fontSize="11" fontWeight="700">{mod.code}</text>
            <text x={x + CARD_W / 2} y={y + 32} textAnchor="middle" fill="#555" fontSize="9">{mod.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ──────────────────────────────────────────────
   연동 아키텍처 섹션
   ────────────────────────────────────────────── */
function WithAIGMPArch() {
  return (
    <section className="waigmp-arch">
      <div className="container">
        <div className="waigmp-arch-inner">
          <div className="waigmp-arch-copy">
            <SectionHead
              eyebrow="INTEGRATION ARCHITECTURE"
              title="withGMP 전 모듈을 한 번에 연결"
              sub={null}
            />
            <p className="waigmp-arch-body">
              with-AI-GMP AI 모듈은 RWS · MES · WMS · withEAI · CMMS · FEMS의 실시간
              데이터를 통합 조회합니다. 사일로 없이 전 공정 데이터를 AI가 연결합니다.
            </p>
            <ul className="waigmp-arch-list">
              {ARCH_MODULES.map((mod) => (
                <li key={mod.code} style={{ '--mod-color': mod.color }}>
                  <span className="waigmp-arch-dot" />
                  <strong>{mod.code}</strong> {mod.label}
                </li>
              ))}
            </ul>
          </div>
          <div className="waigmp-arch-diagram">
            <WithAIGMPArchDiagram />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   역할별 활용 시나리오 섹션
   ────────────────────────────────────────────── */
function WithAIGMPUseCases() {
  return (
    <section className="waigmp-usecases">
      <div className="container">
        <SectionHead
          eyebrow="USE CASES"
          title="직군별 실제 활용 시나리오"
          sub="AI 챗봇은 현장부터 경영진까지 역할에 맞는 정보를 즉시 제공합니다"
        />
        <div className="waigmp-usecases-grid">
          {USE_CASES.map((uc) => {
            const Icon = uc.icon;
            return (
              <article key={uc.role} className="waigmp-usecase-card" style={{ '--uc-accent': uc.accent }}>
                <div className="waigmp-usecase-icon">
                  <Icon size={24} strokeWidth={1.8} />
                </div>
                <h3 className="waigmp-usecase-role">{uc.role}</h3>
                <ul className="waigmp-usecase-list">
                  {uc.scenarios.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   구축 단계 섹션
   ────────────────────────────────────────────── */
function WithAIGMPProcess() {
  return (
    <section className="waigmp-process">
      <div className="container">
        <SectionHead eyebrow="DELIVERY APPROACH" title="단계적이고 안정적인 구축 전략" />
        <div className="waigmp-process-steps">
          <div className="waigmp-process-line" aria-hidden="true" />
          {PROCESS_STEPS.map((s) => (
            <div key={s.n} className="waigmp-process-step">
              <div className="waigmp-process-num">{s.n}</div>
              <h3 className="waigmp-process-step-title">{s.t}</h3>
              <p className="waigmp-process-step-desc">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   CTA 섹션
   ────────────────────────────────────────────── */
function WithAIGMPCTA({ onNavigate }) {
  return (
    <section className="waigmp-cta">
      <svg aria-hidden="true" className="waigmp-cta-bg" viewBox="0 0 1600 400" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="waigmp-cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="#fff" strokeWidth="0.5" />
          </pattern>
          <radialGradient id="waigmp-cta-glow" cx="70%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.4" />
            <stop offset="100%" stopColor="transparent" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect width="1600" height="400" fill="url(#waigmp-cta-grid)" />
        <rect width="1600" height="400" fill="url(#waigmp-cta-glow)" />
        <path
          d="M-50 240 C 320 180, 540 320, 880 260 S 1340 220, 1700 260"
          fill="none" stroke="#fff" strokeOpacity="0.3" strokeWidth="2"
        />
      </svg>
      <div className="container">
        <div className="waigmp-cta-inner">
          <h2 className="waigmp-cta-title">
            GMP 현장에 AI를 도입할 준비가 되셨나요?
          </h2>
          <p className="waigmp-cta-desc">
            withGMP 플랫폼 위에서 즉시 작동하는 AI 챗봇 모듈,
            ㈜새힘정보기술이 도입부터 운영까지 함께합니다.
          </p>
          <div className="waigmp-cta-actions">
            <button type="button" className="waigmp-cta-btn waigmp-cta-btn-primary" onClick={() => onNavigate('/contact')}>
              도입 문의 <ArrowRight size={16} />
            </button>
            <button type="button" className="waigmp-cta-btn waigmp-cta-btn-ghost" onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' }); onNavigate('/solution/withgmp.html'); }}>
              withGMP 전체 보기
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────
   페이지 진입 시 상단 스크롤 처리 및 섹션 조합
   ────────────────────────────────────────────── */
export default function WithAIGMPOverview({ onNavigate }) {
  useEffect(() => {
    // [기능] 페이지 진입 시 항상 상단에서 시작하도록 스크롤을 초기화합니다.
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <ScrollNav />
      <WithAIGMPHero onNavigate={onNavigate} />
      <div id="waigmp-sec-features">
        <WithAIGMPFeatures />
      </div>
      <div id="waigmp-sec-chat">
        <WithAIGMPChatDemo />
      </div>
      <div id="waigmp-sec-arch">
        <WithAIGMPArch />
      </div>
      <div id="waigmp-sec-usecases">
        <WithAIGMPUseCases />
      </div>
      <div id="waigmp-sec-process">
        <WithAIGMPProcess />
      </div>
      <div id="waigmp-sec-cta">
        <WithAIGMPCTA onNavigate={onNavigate} />
      </div>
    </>
  );
}
